import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { aiSkillsClient } from '@terra/shared-rest-clients-ai-skills';
import type {
  AiCreateSkillBody,
  CreateSkillBody,
  ListSkillsQuery,
  PublishSkillBody,
  RollbackSkillBody,
  SkillsContract,
  UpdateSkillDraftBody,
} from '@terra/shared-contracts-ai-skills';
import { callSkillsEndpoint } from '../internal/terra-api-wrapper';

const SKILLS_QUERY_KEY = 'query.aiSkills';
const SKILLS_STALE_TIME_MS = 30000;
const SKILLS_GC_TIME_MS = 5 * 60 * 1000;

export type ListSkillsQueryInput = Partial<ListSkillsQuery>;

export const useSkills = (query: ListSkillsQueryInput = {}) => {
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: [SKILLS_QUERY_KEY],
      exact: false,
    });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [SKILLS_QUERY_KEY, 'list', query],
    queryFn: () =>
      callSkillsEndpoint<typeof SkillsContract.listSkills>({
        endpointFn: () => aiSkillsClient.listSkills({ query }),
      }),
    placeholderData: keepPreviousData,
    staleTime: SKILLS_STALE_TIME_MS,
    gcTime: SKILLS_GC_TIME_MS,
  });

  return {
    skills: data?.skills ?? [],
    total: data?.total ?? 0,
    isLoading,
    error,
    refetch,
    invalidate,
  };
};

export const useSkill = ({
  skillId,
  applicationId,
  enabled = true,
}: {
  skillId: string;
  applicationId?: string | null;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const scopeQuery = applicationId ? { applicationId } : {};

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: [SKILLS_QUERY_KEY, 'detail', skillId],
      exact: false,
    });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [SKILLS_QUERY_KEY, 'detail', skillId, applicationId ?? null],
    queryFn: () =>
      callSkillsEndpoint<typeof SkillsContract.getSkill>({
        endpointFn: () =>
          aiSkillsClient.getSkill({
            params: { skillId },
            query: scopeQuery,
          }),
      }),
    enabled: enabled && Boolean(skillId),
    staleTime: SKILLS_STALE_TIME_MS,
    gcTime: SKILLS_GC_TIME_MS,
  });

  return {
    skill: data ?? null,
    isLoading,
    error,
    refetch,
    invalidate,
  };
};

export const useSkillVersions = ({
  skillId,
  applicationId,
  enabled = true,
}: {
  skillId: string;
  applicationId?: string | null;
  enabled?: boolean;
}) => {
  const scopeQuery = applicationId ? { applicationId } : {};
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [SKILLS_QUERY_KEY, 'versions', skillId, applicationId ?? null],
    queryFn: () =>
      callSkillsEndpoint<typeof SkillsContract.listSkillVersions>({
        endpointFn: () =>
          aiSkillsClient.listSkillVersions({
            params: { skillId },
            query: scopeQuery,
          }),
      }),
    enabled: enabled && Boolean(skillId),
    staleTime: SKILLS_STALE_TIME_MS,
    gcTime: SKILLS_GC_TIME_MS,
  });

  return {
    versions: data?.versions ?? [],
    isLoading,
    error,
    refetch,
  };
};

export const useSkillAudit = ({
  skillId,
  applicationId,
  enabled = true,
}: {
  skillId: string;
  applicationId?: string | null;
  enabled?: boolean;
}) => {
  const scopeQuery = applicationId ? { applicationId } : {};
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [SKILLS_QUERY_KEY, 'audit', skillId, applicationId ?? null],
    queryFn: () =>
      callSkillsEndpoint<typeof SkillsContract.listSkillAudit>({
        endpointFn: () =>
          aiSkillsClient.listSkillAudit({
            params: { skillId },
            query: scopeQuery,
          }),
      }),
    enabled: enabled && Boolean(skillId),
    staleTime: SKILLS_STALE_TIME_MS,
    gcTime: SKILLS_GC_TIME_MS,
  });

  return {
    entries: data?.entries ?? [],
    isLoading,
    error,
    refetch,
  };
};

export const useSkillsMutations = ({
  applicationId,
}: { applicationId?: string | null } = {}) => {
  const queryClient = useQueryClient();
  const scopeQuery = applicationId ? { applicationId } : {};

  const invalidateAll = () =>
    queryClient.invalidateQueries({
      queryKey: [SKILLS_QUERY_KEY],
      exact: false,
    });

  const createSkill = useMutation({
    mutationFn: (body: CreateSkillBody) =>
      callSkillsEndpoint<typeof SkillsContract.createSkill>({
        endpointFn: () => aiSkillsClient.createSkill({ body }),
      }),
    onSuccess: () => {
      void invalidateAll();
    },
  });

  const aiCreateSkill = useMutation({
    mutationFn: (body: AiCreateSkillBody) =>
      callSkillsEndpoint<typeof SkillsContract.aiCreateSkill>({
        endpointFn: () => aiSkillsClient.aiCreateSkill({ body }),
      }),
    onSuccess: () => {
      void invalidateAll();
    },
  });

  const updateSkillDraft = useMutation({
    mutationFn: ({
      skillId,
      body,
    }: {
      skillId: string;
      body: UpdateSkillDraftBody;
    }) =>
      callSkillsEndpoint<typeof SkillsContract.updateSkillDraft>({
        endpointFn: () =>
          aiSkillsClient.updateSkillDraft({
            params: { skillId },
            query: scopeQuery,
            body,
          }),
      }),
    onSuccess: () => {
      void invalidateAll();
    },
  });

  const publishSkill = useMutation({
    mutationFn: ({
      skillId,
      body,
    }: {
      skillId: string;
      body?: PublishSkillBody;
    }) =>
      callSkillsEndpoint<typeof SkillsContract.publishSkill>({
        endpointFn: () =>
          aiSkillsClient.publishSkill({
            params: { skillId },
            query: scopeQuery,
            body,
          }),
      }),
    onSuccess: () => {
      void invalidateAll();
    },
  });

  const rollbackSkill = useMutation({
    mutationFn: ({
      skillId,
      body,
    }: {
      skillId: string;
      body: RollbackSkillBody;
    }) =>
      callSkillsEndpoint<typeof SkillsContract.rollbackSkill>({
        endpointFn: () =>
          aiSkillsClient.rollbackSkill({
            params: { skillId },
            query: scopeQuery,
            body,
          }),
      }),
    onSuccess: () => {
      void invalidateAll();
    },
  });

  const deleteSkill = useMutation({
    mutationFn: (skillId: string) =>
      callSkillsEndpoint<typeof SkillsContract.deleteSkill>({
        endpointFn: () =>
          aiSkillsClient.deleteSkill({
            params: { skillId },
            query: scopeQuery,
          }),
      }),
    onSuccess: () => {
      void invalidateAll();
    },
  });

  const restoreSkill = useMutation({
    mutationFn: (skillId: string) =>
      callSkillsEndpoint<typeof SkillsContract.restoreSkill>({
        endpointFn: () =>
          aiSkillsClient.restoreSkill({
            params: { skillId },
            query: scopeQuery,
          }),
      }),
    onSuccess: () => {
      void invalidateAll();
    },
  });

  return {
    createSkill,
    aiCreateSkill,
    updateSkillDraft,
    publishSkill,
    rollbackSkill,
    deleteSkill,
    restoreSkill,
    invalidateAll,
  };
};
